import React, { useState } from "react";
import { Maximize2, ScanSearch, Loader2, Check, EyeOff, Crosshair, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/components/FileUpload";

const VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|$)/i;
export const isVideoMedia = (it) => VIDEO_RE.test(it.image_url || "") || (it.target_media_type === "video" && !/\.(jpe?g|png|webp|gif|heic|heif)(\?|$)/i.test(it.image_url || ""));

const LEVELS = { safe: ["Sûr", "#15803D"], borderline: ["Limite", "#B45309"], explicit: ["Explicite", "#B91C1C"], approved: ["Validé par un humain", "#15803D"] };
const CATEGORY_FR = { nudity: "nudité", sexual: "sexuel", suggestive: "suggestif", revealing: "tenue légère", violence: "violence", gore: "gore", hate: "haine", drugs: "drogues", safe: "sûr" };

export const hasAiZone = (it) => !!(it?.verdict?.focus && it.verdict.focus.w > 0 && it.verdict.focus.h > 0);

// Cadre IA : rectangle surligné, reste de la photo assombri (ombre géante rognée par le conteneur), libellé accroché au cadre.
export const AiZoneOverlay = ({ focus, label, itemId }) => {
  const pct = (v) => `${Math.round(v * 1000) / 10}%`;
  const labelBelow = focus.y < 0.12;
  return (
    <div className="pointer-events-none absolute" data-testid={`mod-zone-box-${itemId}`}
      style={{ left: pct(focus.x), top: pct(focus.y), width: pct(focus.w), height: pct(focus.h), borderRadius: 10,
        border: "2px solid var(--yalola-primary)", outline: "1.5px solid rgba(255,255,255,.85)", boxShadow: "0 0 0 200vmax rgba(0,0,0,.62)" }}>
      <span className="absolute left-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold text-white shadow"
        style={{ background: "var(--yalola-primary)", ...(labelBelow ? { top: "calc(100% + 6px)" } : { bottom: "calc(100% + 6px)" }) }}
        data-testid={`mod-zone-label-${itemId}`}>
        <Crosshair size={11} className="inline mr-1 -mt-0.5" />{label || "Zone détectée par l'IA"}
      </span>
    </div>
  );
};

const VerdictPanel = ({ it, zoneOn, setZoneOn, video, canManage, onItemPatched }) => {
  const [busy, setBusy] = useState(false);
  const v = it.verdict || {};
  const [lvlTxt, lvlColor] = LEVELS[v.level] || [v.level || "—", "var(--yalola-text-muted)"];
  const zone = hasAiZone(it);
  const locate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/moderation/${it.id}/locate-zone`);
      onItemPatched?.({ verdict: { ...v, focus: data.focus, focus_label: data.focus_label, focus_level: data.level } });
      if (data.focus) { setZoneOn(true); toast.success("Zone IA localisée sur la photo"); }
      else toast.info(data.level === "safe" ? "L'IA juge maintenant cette photo sûre : aucune zone à signaler" : "L'IA n'a pas pu situer une zone précise sur cette photo");
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 text-sm" data-testid={`mod-viewer-verdict-${it.id}`}>
      <div>
        <div className="text-[11px] uppercase tracking-wide font-bold" style={{ color: "var(--yalola-text-muted)" }}>Verdict IA</div>
        <div className="mt-1 flex items-center gap-2">
          <span className="rounded-full px-2.5 py-0.5 text-[11px] font-bold text-white" style={{ background: lvlColor }} data-testid={`mod-viewer-level-${it.id}`}>{lvlTxt}</span>
          {v.focus_level && v.focus_level !== v.level && <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>ré-analyse : {(LEVELS[v.focus_level] || [v.focus_level])[0]}</span>}
        </div>
        <p className="mt-1.5 leading-snug" data-testid={`mod-viewer-reason-${it.id}`}>{v.reason || "—"}</p>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {(v.categories || []).filter((c) => c !== "safe").map((c) => <span key={c} className="chip text-[10px]">{CATEGORY_FR[c] || c}</span>)}
        </div>
      </div>
      {!video && (
        <div className="rounded-xl p-2.5 space-y-2" style={{ background: "var(--yalola-surface-alt)" }}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs font-semibold flex items-center gap-1"><Crosshair size={13} style={{ color: "var(--yalola-primary)" }} /> Zone IA</div>
              <div className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{zone ? (v.focus_label || "Cadre approximatif estimé par l'IA") : "Aucun cadre pour ce signalement"}</div>
            </div>
            <Switch checked={zone && zoneOn} disabled={!zone} onCheckedChange={setZoneOn} aria-label="Afficher la zone IA" data-testid={`mod-zone-toggle-${it.id}`} />
          </div>
          {!zone && canManage && (
            <Button size="sm" variant="outline" className="rounded-full w-full" onClick={locate} disabled={busy} data-testid={`mod-locate-zone-${it.id}`}>
              {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ScanSearch size={14} className="mr-1" />}{busy ? "Analyse en cours…" : "Localiser la zone (1 appel IA)"}
            </Button>
          )}
          {zone && <div className="text-[10px]" style={{ color: "var(--yalola-text-muted)" }}>Le cadre est une estimation : il indique l'élément qui a pesé le plus dans le verdict.</div>}
        </div>
      )}
    </div>
  );
};

// Visionneuse plein écran : photo entière et nette + cadre IA, verdict, note et décision sans revenir à la carte.
export const ModerationMediaViewer = ({ item: it, open, onOpenChange, canManage, status, note, onNoteChange, onAct, onItemPatched }) => {
  const [zoneOn, setZoneOn] = useState(true);
  const [acting, setActing] = useState(null);
  const video = isVideoMedia(it);
  const src = resolveMediaUrl(it.image_url);
  const decide = async (action) => {
    setActing(action);
    const ok = await onAct?.(action);
    setActing(null);
    if (ok !== false) onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[96vw] max-h-[94vh] overflow-y-auto p-0 gap-0 border-0" data-testid={`mod-viewer-${it.id}`}>
        <DialogTitle className="sr-only">Aperçu du média signalé</DialogTitle>
        <DialogDescription className="sr-only">Photo entière avec la zone détectée par l'IA, verdict et décision.</DialogDescription>
        <div className="grid md:grid-cols-[minmax(0,1fr)_300px]">
          <div className="relative grid place-items-center bg-black min-h-[40vh] md:min-h-[60vh] p-2 md:p-4">
            {video ? (
              <video src={src} controls playsInline className="max-h-[50vh] md:max-h-[78vh] max-w-full rounded-lg" data-testid={`mod-viewer-video-${it.id}`} />
            ) : (
              <div className="relative inline-block overflow-hidden rounded-lg">
                <img src={src} alt="" className="block max-h-[50vh] md:max-h-[78vh] max-w-full object-contain" data-testid={`mod-viewer-img-${it.id}`} />
                {zoneOn && hasAiZone(it) && <AiZoneOverlay focus={it.verdict.focus} label={it.verdict.focus_label} itemId={it.id} />}
              </div>
            )}
          </div>
          <div className="p-4 flex flex-col gap-3 md:overflow-y-auto md:max-h-[85vh]" style={{ background: "var(--yalola-surface)" }}>
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{it.target_type} · {(it.target_id || "").slice(0, 8)}{it.target_user?.name ? ` · ${it.target_user.name}` : ""}</div>
            <VerdictPanel it={it} zoneOn={zoneOn} setZoneOn={setZoneOn} video={video} canManage={canManage} onItemPatched={onItemPatched} />
            {status === "pending" ? (
              <div className="mt-auto space-y-2 pt-2 border-t" data-testid={`mod-viewer-actions-${it.id}`}>
                <Input value={note || ""} onChange={(e) => onNoteChange?.(e.target.value)} placeholder="Note interne (optionnelle)…" className="text-sm h-9" data-testid={`mod-viewer-note-${it.id}`} />
                <Button size="sm" className="pill-primary rounded-full w-full" onClick={() => decide("approve")} disabled={!canManage || !!acting} data-testid={`mod-viewer-approve-${it.id}`}>
                  {acting === "approve" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />} Garder le contenu
                </Button>
                <Button size="sm" variant="outline" className="rounded-full w-full" style={{ color: "#B91C1C", borderColor: "#FCA5A5" }} onClick={() => decide("reject")} disabled={!canManage || !!acting} data-testid={`mod-viewer-reject-${it.id}`}>
                  {acting === "reject" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <EyeOff size={14} className="mr-1" />} Masquer le contenu
                </Button>
              </div>
            ) : (
              <div className="mt-auto text-xs pt-2 border-t" style={{ color: "var(--yalola-text-muted)" }} data-testid={`mod-viewer-resolved-${it.id}`}>
                {status === "approved" ? "Déjà gardé (jugé conforme)." : "Déjà masqué."}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Vignette de la carte : floue par défaut (nette au survol), bouton « Voir en grand », puce « Zone IA » si un cadre existe.
export const ModerationMedia = ({ item: it, ...viewerProps }) => {
  const [open, setOpen] = useState(false);
  if (!it.image_url) return null;
  const src = resolveMediaUrl(it.image_url);
  if (it.media_type === "audio") {
    // Lot 657 — fichier audio signalé (ex. prière vocale) : lecteur direct, pas de visionneuse image.
    return (
      <div className="mt-2 rounded-xl p-2 flex items-center gap-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`mod-media-audio-${it.id}`}>
        <span className="text-lg" aria-hidden>🎙️</span>
        <audio controls preload="none" src={src} className="w-full h-9" data-testid={`mod-media-audio-player-${it.id}`} />
      </div>
    );
  }
  const video = isVideoMedia(it);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="group relative mt-2 block w-full overflow-hidden rounded-lg text-left" aria-label="Voir le média en grand" data-testid={`mod-media-open-${it.id}`}>
        {video
          ? <div className="relative grid place-items-center bg-black max-h-56 h-40"><video src={src} muted playsInline preload="metadata" className="h-full w-full object-cover blur-sm group-hover:blur-none transition-all" data-testid={`mod-media-thumb-${it.id}`} /><Play size={28} className="absolute text-white" fill="white" /></div>
          : <img src={src} alt="" className="w-full rounded-lg object-cover max-h-56 blur-sm group-hover:blur-none transition-all" data-testid={`mod-media-thumb-${it.id}`} />}
        {hasAiZone(it) && (
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow" style={{ background: "var(--yalola-primary)" }} data-testid={`mod-zone-chip-${it.id}`}>
            <Crosshair size={11} /> Zone IA
          </span>
        )}
        <span className="absolute right-2 bottom-2 inline-flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white opacity-90 group-hover:opacity-100 transition-opacity">
          <Maximize2 size={12} /> Voir en grand
        </span>
      </button>
      {open && <ModerationMediaViewer item={it} open={open} onOpenChange={setOpen} {...viewerProps} />}
    </>
  );
};
